import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {IconEditor} from 'pages/aiAutomation/flowBuilder/IconEditor';
import LocalizedTextField from './LocalizedTextField';
import ReminderCatalogTabs from './ReminderCatalogTabs';

const QUICK_DAYS = [28, 14, 7, 3, 1];

// Per-reminder announcement content, ordered to match the full screen
// announcement editor. Subtitle is rich text; footer multiline.
const COPY_FIELDS = [
  {key: 'title', label: 'Title'},
  {key: 'subtitle', label: 'Subtitle', richText: true},
  {key: 'headerBadgeText', label: 'Header badge'},
  {key: 'footerText', label: 'Footer', multiline: true},
  {key: 'primaryCtaLabel', label: 'Action button'},
  {key: 'secondaryCtaLabel', label: 'Close button'},
];

const emptyCopy = () =>
  COPY_FIELDS.reduce(
    (acc, copy) => ({...acc, [copy.key]: {EN: '', FR: ''}}),
    {},
  );

const newReminder = () => ({
  enabled: true,
  daysBeforeExpiry: 7,
  copy: emptyCopy(),
  catalogTabs: [
    {
      catalogKey: undefined,
      isDefault: true,
      applyDiscount: false,
      discounts: [],
    },
  ],
});

const PlanRemindersSection = ({
  form,
  catalogItems,
  currencyOptions,
  discountTypes,
  onTranslate,
  loadingTranslation,
}) => (
  <Card
    type="inner"
    title="Renewal Reminders"
    style={{marginBottom: 24}}
    extra={
      <Space>
        <span>Enabled</span>
        <Form.Item
          name="reminderSettingsEnabled"
          valuePropName="checked"
          noStyle>
          <Switch />
        </Form.Item>
      </Space>
    }>
    {/* Popup behaviour — shared across every reminder in this setting. */}
    <Row gutter={16}>
      <Col xs={24} md={8}>
        <Form.Item
          label="Max show count"
          name="reminderMaxShowCount"
          tooltip="How many times the reminder popup can be shown to a user.">
          <InputNumber min={1} max={100} style={{width: '100%'}} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Auto-close after (seconds)"
          name="reminderAutoCloseSec"
          tooltip="Automatically dismiss the popup after this many seconds. Leave empty to keep it open until the user acts.">
          <InputNumber min={0} style={{width: '100%'}} placeholder="e.g. 10" />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Dismissible"
          name="reminderDismissible"
          valuePropName="checked"
          tooltip="Allow the user to dismiss the popup.">
          <Switch />
        </Form.Item>
      </Col>
    </Row>

    {/* Shared features — shown on the popup after the title and subtitle. */}
    <div style={{marginTop: 8, marginBottom: 16}}>
      <strong>Features</strong>
      <p style={{color: '#8b82a7', marginBottom: 8}}>
        Shown on the reminder popup, after the title and subtitle.
      </p>
      <Form.List name="reminderFeatures">
        {(fields, {add, remove}) => (
          <div>
            {!fields.length ? (
              <p style={{color: '#8b82a7'}}>No features yet.</p>
            ) : null}
            {fields.map(field => (
              <div
                key={field.key}
                style={{
                  border: '1px solid #efefef',
                  borderRadius: 8,
                  padding: '8px 16px 16px',
                  marginBottom: 16,
                }}>
                <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                </div>
                <Row gutter={16} align="top">
                  <Col xs={24} lg={6}>
                    <Form.Item
                      label="Icon"
                      name={[field.name, 'icon']}
                      tooltip="Upload the icon shown next to this feature.">
                      <IconEditor folder="subscription-plan-features" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={18}>
                    <LocalizedTextField
                      label="Label"
                      required
                      fieldName={[field.name, 'label']}
                      watchName={['reminderFeatures', field.name, 'label']}
                      onTranslate={onTranslate}
                      loadingTranslation={loadingTranslation}
                    />
                  </Col>
                </Row>
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add({icon: '', label: {EN: '', FR: ''}})}>
              Add feature
            </Button>
          </div>
        )}
      </Form.List>
    </div>

    {/* Reminder schedules — each fires N days before expiry with its own offer. */}
    <div style={{marginTop: 8}}>
      <strong>Reminders</strong>
      <p style={{color: '#8b82a7', marginBottom: 8}}>
        Each reminder fires a number of days before expiry, with its own catalog
        tabs and discounts.
      </p>
      <Form.List name="reminders">
        {(fields, {add, remove}) => (
          <div>
            {!fields.length ? (
              <p style={{color: '#8b82a7'}}>
                No reminders configured. Add one to schedule renewal nudges.
              </p>
            ) : null}
            {fields.map(field => (
              <Card
                key={field.key}
                size="small"
                style={{marginBottom: 16, borderColor: '#e9e4f5'}}
                title={
                  <Space>
                    <Form.Item
                      name={[field.name, 'enabled']}
                      valuePropName="checked"
                      noStyle>
                      <Switch size="small" />
                    </Form.Item>
                    <span>Reminder</span>
                  </Space>
                }
                extra={
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                }>
                <Form.Item name={[field.name, 'id']} hidden>
                  <Input />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} md={12} lg={10}>
                    <Form.Item
                      label="Days before expiry"
                      name={[field.name, 'daysBeforeExpiry']}
                      rules={[
                        {required: true, message: 'Required'},
                        {
                          type: 'number',
                          min: 1,
                          message: 'Must be greater than 0',
                        },
                      ]}>
                      <InputNumber min={1} style={{width: '100%'}} />
                    </Form.Item>
                    <Space wrap style={{marginBottom: 12}}>
                      {QUICK_DAYS.map(day => (
                        <Button
                          key={day}
                          size="small"
                          onClick={() =>
                            form.setFieldValue(
                              ['reminders', field.name, 'daysBeforeExpiry'],
                              day,
                            )
                          }>
                          {day}d
                        </Button>
                      ))}
                    </Space>
                  </Col>
                </Row>

                {/* Announcement copy shown on this reminder's popup. */}
                <div style={{marginTop: 8, marginBottom: 16}}>
                  <strong>Announcement copy</strong>
                  <p style={{color: '#8b82a7', marginBottom: 8}}>
                    Shown on this reminder&apos;s popup.
                  </p>
                  {COPY_FIELDS.map(copy => (
                    <LocalizedTextField
                      key={copy.key}
                      label={copy.label}
                      multiline={copy.multiline}
                      richText={copy.richText}
                      fieldName={[field.name, 'copy', copy.key]}
                      watchName={['reminders', field.name, 'copy', copy.key]}
                      onTranslate={onTranslate}
                      loadingTranslation={loadingTranslation}
                    />
                  ))}
                </div>

                <ReminderCatalogTabs
                  reminderName={field.name}
                  catalogItems={catalogItems}
                  currencyOptions={currencyOptions}
                  discountTypes={discountTypes}
                  form={form}
                />
              </Card>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add(newReminder())}>
              Add reminder
            </Button>
          </div>
        )}
      </Form.List>
    </div>
  </Card>
);

export default PlanRemindersSection;
